/**
 * AP-18 Nachtrag 1 — Hilfe: Inhaltsverzeichnis, Thema, Suche und die Verweise aus Masken.
 *
 * Liegt in `lib/` und nicht im Feature, weil Menü (`AppLayout`), Dashboard-Kopf und die
 * „?"-Hilfe der Listen dasselbe Inhaltsverzeichnis fragen — ein Feature-Import von dort wäre ein
 * Querimport (Modulgrenzen AP-21-B).
 *
 * Was ein Benutzer sehen darf, entscheidet das **Backend** je Thema (Baustein, Recht, Stand); das
 * Frontend zeigt nur, was zurückkommt.
 */
import { create } from 'zustand'
import { api } from '@/lib/axios'

export interface HilfeThemaEintrag {
  slug: string
  title: string
  summary: string
  examples: string[]
  status: 'DRAFT' | 'RELEASED'
  /** nur für den Betreiber */
  module?: string | null
  permissions?: string[]
}

export interface HilfeBereich {
  area: string
  title: string
  topics: HilfeThemaEintrag[]
}

export interface HilfeAbschnitt {
  anchor: string
  level: number
  title: string
  body: string
}

export interface HilfeThema {
  area: string
  areaTitle: string
  slug: string
  title: string
  status: 'DRAFT' | 'RELEASED'
  module?: string | null
  permissions?: string[]
  sections: HilfeAbschnitt[]
}

export interface HilfeTreffer {
  area: string
  slug: string
  topicTitle: string
  anchor: string
  title: string
  /** Suchwörter zwischen ⟦ und ⟧ */
  snippet: string
  rank: number
}

export const TREFFER_START = '⟦'
export const TREFFER_ENDE = '⟧'

export const hilfeApi = {
  inhalt: () => api.get<{ data: HilfeBereich[] }>('/help').then((r) => r.data.data),
  thema: (area: string, slug: string) =>
    api.get<{ data: HilfeThema }>(`/help/${encodeURIComponent(area)}/${encodeURIComponent(slug)}`).then((r) => r.data.data),
  suche: (q: string) => api.get<{ data: HilfeTreffer[] }>('/help/search', { params: { q } }).then((r) => r.data.data),
}

interface HilfeZustand {
  /** `null` = noch nicht geladen */
  inhalt: HilfeBereich[] | null
  /** Für wen geladen — wechselt der Benutzer oder die Firma, wird neu geladen. */
  fuer: string | null
  laden: (fuer: string) => Promise<void>
}

/** Das Inhaltsverzeichnis, einmal je Anmeldung geladen. */
export const useHilfe = create<HilfeZustand>((set, get) => ({
  inhalt: null,
  fuer: null,
  laden: async (fuer) => {
    if (get().fuer === fuer) return
    set({ fuer, inhalt: null })
    try {
      const inhalt = await hilfeApi.inhalt()
      if (get().fuer === fuer) set({ inhalt })
    } catch {
      // Ohne Hilfe geht alles andere weiter; der Menüpunkt bleibt dann aus.
      if (get().fuer === fuer) set({ inhalt: [] })
    }
  },
}))

/** Steht dieses Thema (oder dieser Bereich) im Inhaltsverzeichnis des Benutzers? */
export function hilfeSichtbar(inhalt: HilfeBereich[] | null, area: string, slug?: string): boolean {
  const b = inhalt?.find((x) => x.area === area)
  if (!b) return false
  return !slug || b.topics.some((t) => t.slug === slug)
}

/** `bereich/thema#anker` → Pfad und Teile. */
export function hilfePfad(verweis: string): { area: string; slug?: string; anchor?: string; href: string } {
  const [ort, anchor] = verweis.split('#')
  const [area, slug] = ort.split('/')
  return { area, slug, anchor, href: `/hilfe/${area}${slug ? `/${slug}` : ''}${anchor ? `#${anchor}` : ''}` }
}

/**
 * Nachtrag 1, 6.4 — die Verweise der „?"-Hilfen auf ihren Handbuch-Abschnitt, an **einer** Stelle.
 * Ein Test prüft, dass jedes Ziel samt Anker im Handbuch steht.
 */
export const HILFE_VERWEISE = {
  zusammenfassung: 'zeiterfassung/auswertung-und-export#buchungen-auswerten-zusammenfassung',
  tagesarbeitszeiten: 'zeiterfassung/auswertung-und-export#tagesarbeitszeiten-ansehen',
  aktuelleTaetigkeiten: 'zeiterfassung/auswertung-und-export#sehen-wer-gerade-arbeitet-aktuelle-tätigkeiten',
  fehlzeiten: 'zeiterfassung/fehlzeiten#fehlzeiten-in-der-liste-verwalten-erp',
} as const
